import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { blue, green, grey, orange } from '@mui/material/colors';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { releaseNotes } from './releaseNotesData';

const getChangeType = (change: string): number => {
  const lowerChange = change.toLowerCase();
  if (lowerChange.includes('new') || lowerChange.includes('added')) {
    return 1; // Star/Feature - highest priority
  }
  if (
    lowerChange.includes('bug fix') ||
    lowerChange.includes('fixed') ||
    lowerChange.includes('resolve') ||
    lowerChange.includes('invalid')
  ) {
    return 3; // Bug fix - lowest priority
  }
  return 2; // Other/Release - middle priority
};

const getChangeIcon = (change: string) => {
  const lowerChange = change.toLowerCase();
  if (
    lowerChange.includes('bug fix') ||
    lowerChange.includes('fixed') ||
    lowerChange.includes('resolve')
  ) {
    return <BugReportIcon sx={{ fontSize: 16, color: orange[600] }} />;
  }
  if (lowerChange.includes('new') || lowerChange.includes('added')) {
    return <StarIcon sx={{ fontSize: 16, color: green[600] }} />;
  }
  return <NewReleasesIcon sx={{ fontSize: 16, color: blue[600] }} />;
};

const parseVersion = (version: string): [number, number, number] => {
  const match = version.replace(/^v/, '').split('.').map(Number);
  return [match[0] || 0, match[1] || 0, match[2] || 0];
};

const getVersionBumpType = (version: string, index: number): 'major' | 'minor' | 'patch' => {
  if (index >= releaseNotes.length - 1) return 'patch'; // First release, default to patch
  const [curMajor, curMinor] = parseVersion(version);
  const [prevMajor, prevMinor] = parseVersion(releaseNotes[index + 1].version);
  if (curMajor > prevMajor) return 'major';
  if (curMinor > prevMinor) return 'minor';
  return 'patch';
};

const getVersionChipColor = (bumpType: 'major' | 'minor' | 'patch') => {
  if (bumpType === 'major') return 'warning';
  if (bumpType === 'minor') return 'success';
  return 'primary';
};

const ReleaseNotes = () => {
  const navigate = useNavigate();

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 4,
        '@media print': {
          maxWidth: 'none',
          py: 0,
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          '@media print': {
            p: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            '@media print': {
              mb: 2,
            },
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              '@media print': {
                display: 'none',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              '@media print': {
                fontSize: '1.5rem',
                mb: 1,
              },
            }}
          >
            Version History
          </Typography>
        </Box>

        <Stack spacing={3}>
          {releaseNotes.map((release, index) => (
            <Card
              key={release.version}
              elevation={1}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: grey[200],
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  elevation: 2,
                  borderColor: blue[200],
                },
                '@media print': {
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header with version and date */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={release.version}
                      color={getVersionChipColor(getVersionBumpType(release.version, index)) as any}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                      }}
                    />
                    {release.title && (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: grey[800],
                          fontSize: '1.1rem',
                        }}
                      >
                        {release.title}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: grey[600],
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {release.date}
                  </Typography>
                </Box>

                {/* Changes list */}
                <Stack spacing={1.5}>
                  {[...release.changes]
                    .sort((a, b) => getChangeType(a) - getChangeType(b))
                    .map((change, changeIndex) => (
                      <Box
                        key={changeIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Box sx={{ mt: 0.25 }}>{getChangeIcon(change)}</Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: grey[700],
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                            flex: 1,
                          }}
                        >
                          {change}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {releaseNotes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body1" color="text.secondary">
              No release notes available yet
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ReleaseNotes;
